import {Close as CloseIcon} from '@mui/icons-material';
import {Box, IconButton} from '@mui/material';
import {FC, useEffect} from 'react';
import {styled} from 'styled-components';
import {A11y, Navigation, Pagination} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';

import {NO_IMAGE_PLACEHOLDER} from '@website_frontend/components/image_carousel';
import {isImageLoaded, preloadImage} from '@website_frontend/lib/image_cache';

interface ImageCarouselFullscreenDesktopProps {
  images: string[];
  title?: string;
  currentSlide: number;
  onClose: () => void;
  paginationConfig: {clickable?: boolean; type: 'bullets' | 'fraction'};
}

export const ImageCarouselFullscreenDesktop: FC<ImageCarouselFullscreenDesktopProps> = props => {
  const {images, title, currentSlide, onClose, paginationConfig} = props;

  // Preload images when component mounts or images/currentSlide changes
  // Preload current slide and adjacent slides for smooth navigation
  useEffect(() => {
    if (images.length === 0) return;

    // Preload current slide and adjacent slides (±2)
    const startIndex = Math.max(0, currentSlide - 2);
    const endIndex = Math.min(images.length - 1, currentSlide + 2);

    for (let i = startIndex; i <= endIndex; i++) {
      const src = images[i];
      if (src && !isImageLoaded(src)) {
        preloadImage(src);
      }
    }
  }, [images, currentSlide]);

  return (
    <FullscreenContainer onClick={onClose}>
      <CloseButtonContainer>
        <IconButton
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
          size="large"
          sx={{
            p: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'saturate(120%) blur(2px)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
          }}
          aria-label="Close"
        >
          <CloseIcon fontSize="large" />
        </IconButton>
      </CloseButtonContainer>
      <FullscreenContent onClick={e => e.stopPropagation()}>
        <FullscreenSwiperWrapper onClick={e => e.stopPropagation()}>
          <Swiper
            modules={[Navigation, Pagination, A11y]}
            slidesPerView={'auto'}
            navigation
            pagination={paginationConfig}
            initialSlide={currentSlide}
            className="fullscreen-swiper"
          >
            {images.length > 0 ? (
              images.map(src => (
                <SwiperSlide key={src}>
                  <FullscreenImageContainer>
                    <img
                      src={src}
                      alt={title || 'Image'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </FullscreenImageContainer>
                </SwiperSlide>
              ))
            ) : (
              <SwiperSlide>
                <FullscreenImageContainer>
                  <img
                    src={NO_IMAGE_PLACEHOLDER}
                    alt={title || 'Image'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </FullscreenImageContainer>
              </SwiperSlide>
            )}
          </Swiper>
        </FullscreenSwiperWrapper>
      </FullscreenContent>
    </FullscreenContainer>
  );
};

ImageCarouselFullscreenDesktop.displayName = 'ImageCarouselFullscreenDesktop';

const FullscreenContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const CloseButtonContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10001;
  padding: 16px;
`;

const FullscreenContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FullscreenSwiperWrapper = styled.div`
  width: 100%;
  height: 100%;
  
  .fullscreen-swiper {
    width: 100%;
    height: 100%;
  }
  
  /* Swiper navigation arrows */
  .fullscreen-swiper .swiper-button-next,
  .fullscreen-swiper .swiper-button-prev {
    --swiper-navigation-size: 44px;
    color: rgba(255, 255, 255, 0.9);
    width: 50px;
    height: 50px;
    border-radius: 9999px;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: saturate(120%) blur(2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    
    &:hover {
      background: rgba(0, 0, 0, 0.7);
    }
  }
  .fullscreen-swiper .swiper-button-next::after,
  .fullscreen-swiper .swiper-button-prev::after {
    font-size: 22px;
  }

  /* Pagination background container */
  .fullscreen-swiper .swiper-pagination {
    z-index: 2;
    width: fit-content;
    margin: 0 auto;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 10px;
    padding: 0px 8px;
    backdrop-filter: saturate(120%) blur(2px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }

  /* Swiper bullets */
  .fullscreen-swiper .swiper-pagination-bullets.swiper-pagination-horizontal, 
  .fullscreen-swiper .swiper-pagination-fraction {
    left: 50%;
    width: auto;
    translate: -50%;
  }
  .fullscreen-swiper .swiper-pagination-bullet {
    background: rgba(255, 255, 255, 0.5);
    opacity: 1;
  }
  .fullscreen-swiper .swiper-pagination-bullet-active {
    background: rgba(255, 255, 255, 0.9);
  }

  /* Swiper fraction */
  .fullscreen-swiper .swiper-pagination-fraction {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 600;
    font-size: 14px;
  }
`;

const FullscreenImageContainer = styled(Box)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
`;
